import React, { useEffect, useState, useCallback, useRef, lazy, Suspense } from 'react'
import { logger } from '@/utils/logger'
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from 'react-router-dom'
import './App.css'
import ErrorBoundary from './components/ErrorBoundary'
import BasketErrorBoundary from './components/BasketErrorBoundary'
import ErrorNotification from './components/ErrorNotification'
import useErrorNotificationStore from './context/ErrorNotificationStore'
import Basket from './components/Basket'
import InfoScreen from './components/InfoScreen'
import Footer from './components/Footer'
import CookieBanner from './components/CookieBanner'
import InternalTrafficNotice from './components/InternalTrafficNotice'
import TrackingRedirect from './components/TrackingRedirect'
import { TrackingLoadingState } from './components/TrackingRequestState'
import KibuClubPopup from './components/KibuClubPopup'
import useKibuClubPopupStore from './context/KibuClubPopupStore'
import DebugHolder from './components/DebugHolder'
import DiscountNotification from './components/DiscountNotification'
import ScrollToTop, { ScrollContainerContext } from './components/ScrollToTop'
import Nav from './components/nav/Nav'
import useCookieConsentStore from './context/CookieConsentStore'
import useRegionStore from './context/RegionStore'
import useCountryDetailsStore from './context/CountryDetailsStore'
import { initializeAnalytics, updateAnalyticsConsent, removeTrackingScripts } from './utils/cookieConsent'
import { isGlobalRoute } from './utils/regionDetection'
import CountryMismatchModal from './components/CountryMismatchModal'
import RegionSelectorModal from './components/RegionSelectorModal'
import useAvailableRegionsStore from './context/AvailableRegionsStore'
import RegionWrapper from './components/RegionWrapper'
import BackToTop from './components/BackToTop'
import CustomColoursContainer from './components/CustomColoursContainer'
import useCustomColoursStore from './context/CustomColoursStore'
import useDiscountRevalidation from './hooks/useDiscountRevalidation'
import useDiscountCapture from './hooks/useDiscountCapture'
import useDiscountStore from './context/DiscountStore'
import StockUnavailableLightbox from './components/StockUnavailableLightbox'
import useStockNoticeStore from './context/StockNoticeStore'
import JourneyPopupRuntime from './components/JourneyPopupRuntime'
import { captureAttributionFromLocation } from './utils/attribution'
import { trackServerPageView } from './utils/tracking'
// Lazy load legal pages - users rarely visit these on initial load
const Legal = lazy(() => import('./pages/Legal'))
const Tracking = lazy(() => import('./pages/Tracking'))
const TrackingUpgradesPreview = process.env.NODE_ENV === 'development' ? lazy(() => import('./pages/TrackingUpgradesPreview')) : null
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'))
const KibuCareTerms = lazy(() => import('./pages/KibuCareTerms'))
const TermsOfService = lazy(() => import('./pages/TermsOfService'))
const DeliveryAndReturns = lazy(() => import('./pages/DeliveryAndReturns'))
const AbandonedUnsubscribe = lazy(() => import('./pages/AbandonedUnsubscribe'))
const PostPurchaseSurvey = lazy(() => import('./pages/PostPurchaseSurvey'))

// Lazy load blog pages - secondary content
const Blog = lazy(() => import('./pages/blog/BlogHome'))
const BlogPost = lazy(() => import('./pages/blog/BlogPost'))

// Lazy load build instructions - users rarely visit, includes HLS.js dependency
const BuildInstructionsRoute = lazy(() => import('./pages/BuildInstructionsRoute'))

// Lazy load sustainability page
const Sustainability = lazy(() => import('./pages/Sustainability'))
const DIYCollaborationEnded = lazy(() => import('./pages/DIYCollaborationEnded'))

// Lazy load linktree page
const LinkTree = lazy(() => import('./pages/LinkTree'))
const InstagramLinkRedirect = lazy(() => import('./pages/InstagramLinkRedirect'))

// Lazy load contact form page
const Contact = lazy(() => import('./pages/Contact'))
const Investor = lazy(() => import('./pages/Investor'))

// Inner component that handles all routing logic
const AppContent = () => {
	const [fullscreenOverlayActive, setFullscreenOverlayActive] = useState<boolean>(false)
	const [hideOverflowActive, setHideOverflowActive] = useState<boolean>(false)
	const { consent } = useCookieConsentStore()
	// Seed with the correct nav height for the current viewport (mobile nav is 84px,
	// desktop is 104px — see Nav.tsx h-[84px] md:h-[104px]). Matching the value the
	// ResizeObserver will measure avoids a ~20px content shift (CLS) on mobile load.
	const [navHeight, setNavHeight] = useState(
		() => (typeof window !== 'undefined' && window.innerWidth < 768 ? 84 : 104)
	)
	const [isNavVisible, setIsNavVisible] = useState(true)
	const [isFooterRegionModalOpen, setIsFooterRegionModalOpen] = useState(false)

	// Error notification state
	const { errors, removeError, addNetworkError, clearAllErrors } = useErrorNotificationStore()
	const {
		isOpen: isStockNoticeOpen,
		title: stockNoticeTitle,
		message: stockNoticeMessage,
		items: stockNoticeItems,
		removedFromBasket: stockNoticeRemoved,
		hide: hideStockNotice
	} = useStockNoticeStore()
	const [lastScrollY, setLastScrollY] = useState(0)
	const [navVisibilityOverride, setNavVisibilityOverride] = useState<boolean | null>(null)
	const { fetchAllCountries } = useCountryDetailsStore()
	const contentRef = useRef<HTMLDivElement>(null)
	const [isRegionModalOpen, setIsRegionModalOpen] = useState(false)
	const location = useLocation()
	const lastTrackedRouteRef = useRef<string | null>(null)
	const { urlRegion, showRegionModal, chosenRegion, detectedRegion, acceptedRegion, getUrlRegion, setShowRegionModal, setDetectedRegion, setChosenRegion } = useRegionStore()
	const { isRegionAvailable } = useAvailableRegionsStore()
	const [isDetecting, setIsDetecting] = useState(true)
	const {
		isOpen: customColoursOpen,
		customColourProduct,
		customComponents,
		updateCustomComponent,
		setOpen: setCustomColoursOpen
	} = useCustomColoursStore()
	const isBlogPage = location.pathname.startsWith('/blog')
	const { showPopup: showKibuClubPopup, shouldShowPopup } = useKibuClubPopupStore()
	const { discountCode } = useDiscountStore()

	// Initialize discount revalidation
	useDiscountRevalidation()
	useDiscountCapture()

	// Show Kibu Club popup after delay for GB users
	useEffect(() => {
		// console.log('🎯 Kibu Club Popup Check:', {
		// 	detectedRegion,
		// 	shouldShow: shouldShowPopup(),
		// 	isGB: detectedRegion === 'GB'
		// })

		// Only show for GB region (temporarily disabled for testing - shows for all regions)
		if (detectedRegion && shouldShowPopup() && !discountCode?.valid) {
			// console.log('✅ Showing Kibu Club popup in 5 seconds...')
			const timer = setTimeout(() => {
				// console.log('🎉 Triggering Kibu Club popup now!')
				showKibuClubPopup()
			}, 10000) // 5 second delay

			return () => clearTimeout(timer)
		} else {
			// console.log('❌ Not showing Kibu Club popup')
		}
	}, [detectedRegion, showKibuClubPopup, shouldShowPopup, discountCode])

	// Optimize initial render by deferring non-critical operations
	useEffect(() => {
		const timer = setTimeout(() => {
			fetchAllCountries();
		}, 1000);
		return () => clearTimeout(timer);
	}, []);

	// Region detection is now handled by RegionWrapper component
	// to avoid duplicate API calls and allow instant page display

	// Update URL region whenever pathname changes and show modal if needed
	useEffect(() => {
		if (!isGlobalRoute(location.pathname)) {
			getUrlRegion(location.pathname);
		}
	}, [location.pathname]);

	// Show modal if URL region doesn't match detected region and hasn't been accepted
	useEffect(() => {
		if (!isGlobalRoute(location.pathname) && detectedRegion && urlRegion) {
			if (urlRegion !== detectedRegion && urlRegion !== chosenRegion) {
				if (isRegionAvailable(detectedRegion) && isRegionAvailable(urlRegion)) {
					logger.debug('component', 'App: Region modal trigger', {
						reason: 'URL Region Change',
						urlRegion,
						detectedRegion,
						chosenRegion,
						timestamp: new Date().toISOString()
					});
					setShowRegionModal(true);
				}
			}
		}
	}, [urlRegion, detectedRegion, chosenRegion, isRegionAvailable, location.pathname]);

	// Set chosen region if not set
	useEffect(() => {
		if (chosenRegion === null && detectedRegion !== null) {
			logger.info('component', 'App: Region selected', {
				reason: 'Initial Detection',
				detectedRegion,
				timestamp: new Date().toISOString()
			});
			setChosenRegion(detectedRegion);
		}
	}, [chosenRegion, detectedRegion])

	// Network status monitoring
	useEffect(() => {
		const handleOnline = () => {
			logger.info('general', 'Network connection restored')
			clearAllErrors() // Clear network errors when back online
		}

		const handleOffline = () => {
			logger.warn('general', 'Network connection lost')
			addNetworkError(
				'No internet connection. Please check your connection and try again.',
				() => window.location.reload()
			)
		}

		window.addEventListener('online', handleOnline)
		window.addEventListener('offline', handleOffline)

		// Check initial connection state
		if (!navigator.onLine) {
			handleOffline()
		}

		return () => {
			window.removeEventListener('online', handleOnline)
			window.removeEventListener('offline', handleOffline)
		}
	}, [addNetworkError, clearAllErrors])

	useEffect(() => {
		let timeout: NodeJS.Timeout | null = null;

		if (fullscreenOverlayActive) {
			setHideOverflowActive(true)
		} else {
			timeout = setTimeout(() => {
				setHideOverflowActive(false)
			}, 200);
		}

		return () => {
			if (timeout) clearTimeout(timeout)
		};
	}, [fullscreenOverlayActive])

	// Handle cookie consent changes
	useEffect(() => {
		if (consent.hasConsented) {
			if (consent.analytics || consent.marketing) {
				initializeAnalytics(consent)
			} else {
				removeTrackingScripts()
			}
			updateAnalyticsConsent(consent)
		}
	}, [consent])

	// Persist attribution parameters (UTM/gclid/fbclid) across route changes.
	useEffect(() => {
		captureAttributionFromLocation()
	}, [location.pathname, location.search])

	// Track SPA route page views after initial page load.
	useEffect(() => {
		const routeKey = `${location.pathname}${location.search}`
		const canTrackRouteView = consent.hasConsented && (consent.analytics || consent.marketing)

		if (!canTrackRouteView) {
			return
		}

		if (routeKey === lastTrackedRouteRef.current) {
			return
		}

		lastTrackedRouteRef.current = routeKey

		if (consent.hasConsented && consent.analytics && window.gtag) {
			window.gtag('event', 'page_view', {
				page_path: routeKey,
				page_location: window.location.href,
				page_title: document.title,
			})
		}

		if (consent.hasConsented && consent.marketing && window.fbq) {
			window.fbq('track', 'PageView')
		}
		if (consent.hasConsented && consent.analytics) {
			trackServerPageView()
		}
	}, [location.pathname, location.search, consent.hasConsented, consent.analytics, consent.marketing])

	const handleScroll = useCallback(() => {
		if (!contentRef.current) return;

		const currentScrollY = contentRef.current.scrollTop;
		const scrollHeight = contentRef.current.scrollHeight;
		const clientHeight = contentRef.current.clientHeight;
		const remainingScroll = scrollHeight - (currentScrollY + clientHeight);

		if (currentScrollY < navHeight || remainingScroll < navHeight * 2) {
			setIsNavVisible(true);
			setLastScrollY(currentScrollY);
			return;
		}

		const scrollDelta = currentScrollY - lastScrollY;
		if (Math.abs(scrollDelta) < 10) return;

		setIsNavVisible(scrollDelta < 0);
		setLastScrollY(currentScrollY);
	}, [lastScrollY, navHeight]);

	useEffect(() => {
		const contentElement = contentRef.current;
		if (!contentElement) return;

		contentElement.addEventListener('scroll', handleScroll, { passive: true });

		return () => {
			contentElement.removeEventListener('scroll', handleScroll);
		};
	}, [handleScroll]);

	return (
		<ScrollContainerContext.Provider value={{ contentRef, navHeight }}>
			<div className={`app relative bg-background transition-all ${hideOverflowActive && ''}`}>
				{/* Optimize initial paint by moving fixed positioning to a separate element */}
				<div className="min-h-screen bg-background">
					<div
						ref={contentRef}
						className="fixed inset-0 overflow-y-auto bg-background will-change-transform min-h-screen"
						style={{
							paddingTop: (navVisibilityOverride !== null ? navVisibilityOverride : isNavVisible) ? `${navHeight}px` : '0px',
							transition: 'padding-top 700ms cubic-bezier(0.4, 0, 0.2, 1)',
						}}
					>
						<div className="min-h-full relative">
							<ScrollToTop />
							<Routes>
								{/* Global routes that don't require region */}
								<Route path="/legal/*" element={<Suspense fallback={<div className="h-screen flex items-center justify-center">Loading...</div>}><Legal /></Suspense>} />
								<Route path="legal/privacy-policy" element={<Suspense fallback={<div className="h-screen flex items-center justify-center">Loading...</div>}><PrivacyPolicy /></Suspense>} />
								<Route path="legal/kibu-care-terms" element={<Suspense fallback={<div className="h-screen flex items-center justify-center">Loading...</div>}><KibuCareTerms /></Suspense>} />
								<Route path="legal/terms-of-service" element={<Suspense fallback={<div className="h-screen flex items-center justify-center">Loading...</div>}><TermsOfService /></Suspense>} />
								<Route path="legal/delivery-and-returns" element={<Suspense fallback={<div className="h-screen flex items-center justify-center">Loading...</div>}><DeliveryAndReturns /></Suspense>} />
								<Route path="blog" element={<Suspense fallback={<div className="h-screen flex items-center justify-center">Loading...</div>}><Blog /></Suspense>} />
								<Route path="blog/:slug" element={<Suspense fallback={<div className="h-screen flex items-center justify-center">Loading...</div>}><BlogPost /></Suspense>} />
								<Route path="build" element={<Suspense fallback={<div className="h-screen flex items-center justify-center">Loading...</div>}><BuildInstructionsRoute navHeight={navHeight} /></Suspense>} />
								<Route path="build/:productId" element={<Suspense fallback={<div className="h-screen flex items-center justify-center">Loading...</div>}><BuildInstructionsRoute navHeight={navHeight} /></Suspense>} />
								<Route path="sustainability" element={<Suspense fallback={<div className="h-screen flex items-center justify-center">Loading...</div>}><Sustainability /></Suspense>} />
								<Route path="diy" element={<Suspense fallback={<div className="h-screen flex items-center justify-center">Loading...</div>}><DIYCollaborationEnded /></Suspense>} />
								<Route path="links" element={<Suspense fallback={<div className="h-screen flex items-center justify-center">Loading...</div>}><LinkTree /></Suspense>} />
								<Route path="i/:code" element={<Suspense fallback={<div className="h-screen flex items-center justify-center">Loading...</div>}><InstagramLinkRedirect /></Suspense>} />
								<Route path="tracking/*" element={<TrackingRedirect />} />
								{TrackingUpgradesPreview && <Route path="design/tracking-upgrades" element={<Suspense fallback={<TrackingLoadingState />}><TrackingUpgradesPreview /></Suspense>} />}
								<Route path="track" element={<Suspense fallback={<TrackingLoadingState />}><Tracking /></Suspense>} />
								<Route path="track/:orderNumber" element={<Suspense fallback={<TrackingLoadingState />}><Tracking /></Suspense>} />
								<Route path="contact" element={<Suspense fallback={<div className="h-screen flex items-center justify-center">Loading...</div>}><Contact /></Suspense>} />
								<Route path=":region/contact" element={<Suspense fallback={<div className="h-screen flex items-center justify-center">Loading...</div>}><Contact /></Suspense>} />
								<Route path="investor" element={<Suspense fallback={<div className="h-screen flex items-center justify-center">Loading...</div>}><Investor /></Suspense>} />
								<Route path=":region/investor" element={<Suspense fallback={<div className="h-screen flex items-center justify-center">Loading...</div>}><Investor /></Suspense>} />
								<Route path="unsubscribe" element={<Suspense fallback={<div className="h-screen flex items-center justify-center">Loading...</div>}><AbandonedUnsubscribe /></Suspense>} />
								<Route path="post-purchase-survey" element={<Suspense fallback={<div className="h-screen flex items-center justify-center">Loading...</div>}><PostPurchaseSurvey /></Suspense>} />

								{/* All region-specific routes */}
								<Route path="/*" element={
									<RegionWrapper
										toggleOverlay={setFullscreenOverlayActive}
										navHeight={navHeight}
										setNavVisibilityOverride={setNavVisibilityOverride}
									/>
								} />
							</Routes>

							<Footer
								className="z-10 relative"
								isRegionModalOpen={isFooterRegionModalOpen}
								onRegionModalOpen={() => setIsFooterRegionModalOpen(true)}
							/>

						</div>
					</div>

					{/* Nav overlaid on top with high z-index */}
					<Nav
						isVisible={navVisibilityOverride !== null ? navVisibilityOverride : isNavVisible}
						onHeightChange={setNavHeight}
						className="z-40"
						disableScrollAnimation={isBlogPage}
					/>

					<InfoScreen />

					{/* Basket component */}
					<BasketErrorBoundary>
						<Basket className="z-50" />
					</BasketErrorBoundary>

					{/* Journey popup runtime */}
					<JourneyPopupRuntime />
					<StockUnavailableLightbox
						isOpen={isStockNoticeOpen}
						title={stockNoticeTitle}
						message={stockNoticeMessage}
						items={stockNoticeItems}
						removedFromBasket={stockNoticeRemoved}
						onClose={hideStockNotice}
					/>

					{/* Kibu Club Popup - GB region only */}
					<KibuClubPopup />

					{/* Debug components */}
					<DebugHolder />

					{/* Region Selector Modal - From Nav */}
					{isRegionModalOpen && (
						<RegionSelectorModal onClose={() => setIsRegionModalOpen(false)} />
					)}

					{/* Region Selector Modal - From Footer */}
					{isFooterRegionModalOpen && (
						<RegionSelectorModal onClose={() => setIsFooterRegionModalOpen(false)} />
					)}

					{/* Custom Colours Container */}
					<CustomColoursContainer
						open={customColoursOpen}
						customColourProduct={customColourProduct}
						customComponents={customComponents}
						updateCustomComponent={updateCustomComponent}
						onClose={() => setCustomColoursOpen(false)}
						onComplete={() => setCustomColoursOpen(false)}
						navHeight={navHeight}
					/>

					{/* Modals at the highest z-index */}
					{/* New unified modal handles all 3 region scenarios */}
					<CountryMismatchModal />

					{/* Discount notification - Very high z-index to show above everything */}
					<DiscountNotification />

					{/* CookieBanner moved outside the scrollable container */}
					<CookieBanner />
					<InternalTrafficNotice />

					{/* Error Notifications */}
					<ErrorNotification
						errors={errors}
						onDismiss={removeError}
						position="top-right"
					/>

					<BackToTop className="z-500" contentRef={contentRef} />
				</div>
			</div>
		</ScrollContainerContext.Provider>
	);
};

// Outer component that provides the router
function App() {
	return (
		<ErrorBoundary showDetails={true}>
			<BrowserRouter
				future={{
					v7_startTransition: true,
					v7_relativeSplatPath: true,
				}}>
				<AppContent />
			</BrowserRouter>
		</ErrorBoundary>
	);
}

export default App;
